import type {
  ApiErrorBody,
  CharacterSummaryDTO,
  CreateCharacterBody,
  FirebaseLoginResponse,
  MeResponse,
  RealmDTO,
} from '@jianghu/protocol';
import { API_BASE, getSessionToken, setSessionToken } from './backend';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly fields: Record<string, string> = {},
  ) {
    super(message);
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  let res: Response;
  const headers: Record<string, string> = {};
  if (body !== undefined) headers['content-type'] = 'application/json';
  const token = getSessionToken();
  if (token) headers['authorization'] = `Bearer ${token}`;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      method,
      credentials: 'include',
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch {
    throw new ApiError(0, 'network', 'Cannot reach the server. Check your connection.');
  }
  const text = await res.text();
  let data: unknown = null;
  try {
    data = text ? (JSON.parse(text) as unknown) : null;
  } catch {
    data = null; // a static host answers 404 with an HTML page when no game server is attached
  }
  if (!res.ok) {
    if (res.status === 401) setSessionToken(null); // stale session — force a fresh sign-in
    if (res.status === 404 && !(data as ApiErrorBody | null)?.error) {
      throw new ApiError(404, 'server_unavailable', 'The game server is not running yet.');
    }
    const err = (data as ApiErrorBody | null)?.error;
    throw new ApiError(
      res.status,
      err?.code ?? 'error',
      err?.message ?? `Request failed (${res.status})`,
      err?.fields ?? {},
    );
  }
  return data as T;
}

/** Thin typed wrapper over the REST API (cookie session, Bearer-token fallback). */
export const api = {
  me: () => request<MeResponse>('GET', '/api/auth/me'),
  /** Exchanges a Firebase ID token for a game session (shared account, see firebase-auth.ts). */
  firebaseLogin: (idToken: string) =>
    request<FirebaseLoginResponse>('POST', '/api/auth/firebase', { idToken }).then((r) => {
      setSessionToken(r.sessionToken);
      return r;
    }),
  logout: () => request<{ ok: true }>('POST', '/api/auth/logout').finally(() => setSessionToken(null)),
  realms: () => request<{ realms: RealmDTO[] }>('GET', '/api/realms').then((r) => r.realms),
  characters: (realm: string) =>
    request<{ characters: CharacterSummaryDTO[] }>(
      'GET',
      `/api/characters?realm=${encodeURIComponent(realm)}`,
    ).then((r) => r.characters),
  createCharacter: (body: CreateCharacterBody) =>
    request<{ character: CharacterSummaryDTO }>('POST', '/api/characters', body).then((r) => r.character),
  deleteCharacter: (id: string, confirmName: string) =>
    request<{ ok: true }>('POST', `/api/characters/${encodeURIComponent(id)}/delete`, { confirmName }),
};
